import { useId, useState, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface ConfirmDialogProps {
  readonly title: string;
  readonly body: ReactNode;
  readonly confirmLabel: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  readonly isPending?: boolean;
  readonly error?: string | undefined;
  /** Renders the confirm button as destructive. */
  readonly destructive?: boolean;
  /**
   * Holds the confirm shut because the dialog's own form is incomplete — the
   * merge dialog before a survivor is picked. Distinct from `typeToConfirm`,
   * which is a deliberate speed bump rather than a missing input.
   */
  readonly confirmDisabled?: boolean;
  /**
   * When set, the operator must type this exact string before confirming.
   *
   * Reserved for actions with no undo — the design's "discard job" is the one
   * in this console. Deactivating a food is reversible and deliberately does
   * not use it: a confirmation nobody can fail teaches people to click through.
   */
  readonly typeToConfirm?: string;
}

/** The centre modal. One decision, stated plainly, with the consequence visible. */
export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  onConfirm,
  onCancel,
  isPending = false,
  error,
  destructive = false,
  confirmDisabled = false,
  typeToConfirm,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('');
  const inputId = useId();

  const unconfirmed = typeToConfirm !== undefined && typed !== typeToConfirm;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      <DialogContent aria-label={title} showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription asChild>
            <div>{body}</div>
          </DialogDescription>
        </DialogHeader>

        {typeToConfirm === undefined ? null : (
          <div className="flex flex-col gap-2">
            <Label htmlFor={inputId} className="text-muted-foreground">
              Type <span className="font-mono text-foreground">{typeToConfirm}</span> to confirm
            </Label>
            <Input
              id={inputId}
              value={typed}
              onChange={(event) => {
                setTyped(event.target.value);
              }}
              className="font-mono"
            />
          </div>
        )}

        {error === undefined ? null : (
          <p role="alert" className="text-[13px] leading-relaxed text-err-fg">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'default'}
            onClick={onConfirm}
            disabled={isPending || unconfirmed || confirmDisabled}
          >
            {isPending ? 'Working…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
